import type { RouteObject } from "react-router";
import { useNavigate } from "react-router";

import { Button, ButtonStyle, PageHeading } from "@prequel/shared-components";

import ProviderAdmin from "./ProviderAdmin";
import ProviderDetails from "./ProviderDetails";
import ProviderForm from "./ProviderForm";
import { providerToDraft } from "./ProviderForm/formState";
import ProviderPage, { ProviderDetailsPage, useProviderContext } from "./ProviderPage";
import ProviderSources from "./ProviderSources";
import ProvidersTable from "./ProvidersTable";

import Breadcrumbs from "@/components/Breadcrumbs";

const ProvidersListLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4 pb-16">
        <PageHeading title="Providers" breadcrumbs={<Breadcrumbs />}>
          <Button text="Add Provider" onClick={() => navigate("/import/providers/new")} type={ButtonStyle.SECONDARY} />
        </PageHeading>
        <ProvidersTable />
      </div>
    </div>
  );
};

const ProviderFormNewLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-3xl px-7 py-4">
        <PageHeading
          title="Add Provider"
          onGoBack={() => navigate("/import/providers")}
          breadcrumbs={<Breadcrumbs />}
        />
        <ProviderForm />
      </div>
    </div>
  );
};

const ProviderFormEditLayout = () => {
  const navigate = useNavigate();
  const { provider } = useProviderContext();

  return (
    <div className="h-full flex-col">
      <div className="max-w-3xl px-7 py-4">
        <PageHeading
          title="Edit Provider"
          onGoBack={() => navigate(`/import/providers/${provider.id}`)}
          breadcrumbs={<Breadcrumbs dynamicLabel={provider.name} />}
        />
        <ProviderForm
          providerId={provider.id}
          initialDraft={providerToDraft(provider)}
          cancelPath={`/import/providers/${provider.id}`}
        />
      </div>
    </div>
  );
};

export const providersRouteChildren: RouteObject = {
  path: "providers",
  handle: {
    breadcrumb: () => ({ label: "Providers" }),
  },
  children: [
    {
      index: true,
      element: <ProvidersListLayout />,
    },
    {
      path: "new",
      handle: {
        breadcrumb: () => ({ label: "Add Provider" }),
      },
      element: <ProviderFormNewLayout />,
    },
    {
      path: ":providerID",
      handle: {
        breadcrumb: () => ({
          label: "Provider Details",
          dynamic: true,
        }),
      },
      element: <ProviderPage />,
      children: [
        {
          path: "edit",
          handle: {
            breadcrumb: () => ({ label: "Edit Provider" }),
          },
          element: <ProviderFormEditLayout />,
        },
        {
          element: <ProviderDetailsPage />,
          children: [
            {
              index: true,
              element: <ProviderDetails />,
            },
            // Tabs deliberately have no breadcrumb handle: they are views of the detail
            // page, so all three keep the same trail.
            {
              path: "sources",
              element: <ProviderSources />,
            },
            {
              path: "admin",
              element: <ProviderAdmin />,
            },
          ],
        },
      ],
    },
  ],
};
